import { Controller } from "@hotwired/stimulus"
import Konva from "konva"
import { editorStore } from "../editor/store"
import { metresToPx, pxToMetres } from "../editor/projection"
import {
  createGraph,
  cloneGraph,
  addRoomRect,
  settleVertex,
  removeWall,
  resizeWallAlongAxis,
  boundingBox,
  totalWallLengthM,
} from "../floorplan/graph"
import { snapDraggedVertex } from "../floorplan/snapping"
import { reconcileRooms, outerContour, polygonCentroid, classifyWalls } from "../floorplan/faces"
import { validateFloor, violationsByRoomIndex, loadFloorplanRuleSet } from "../floorplan/rules_internal"
import { ROOM_TYPES, roomLabel, roomColor } from "../floorplan/room_types"
import {
  placeOpening,
  removeOpening,
  updateOpening,
  reconcileOpenings,
  openingSegment,
  OPENING_KINDS,
  roomWindowSun,
} from "../floorplan/openings"
import { computeHeatmap, computeRoomInsolation, insolationLevel, pointInPolygon } from "../editor/heatmap"
import { profileFor, bandHoursAt, bandHoursInPolygon, scorePlacement, fitFor } from "../floorplan/advisor"
import { computeEstimate, CATALOGUES, DEFAULT_SELECTIONS, DEFAULT_PARAMS } from "../floorplan/materials"
import { localToPlotPolygon, polygonMin } from "../editor/geometry"
import { estimateUtcOffsetHours, sunArcSamples } from "../editor/sun"

// Per-room sun diagnosis → badge styling.
const INSOLATION_BADGE = {
  low: { dot: "🔴", cls: "text-rose-600" },
  medium: { dot: "🟡", cls: "text-amber-600" },
  high: { dot: "🟢", cls: "text-emerald-600" },
}

// Room types for which having no exterior window is normal — these are NOT
// flagged (Stage 14b F3), to cut the repeated "нет окон наружу" noise from a
// dense plan. Every other type (incl. the ambiguous default "other") still gets
// the hint, since a habitable/untyped room usually wants daylight.
const NO_WINDOW_OK_TYPES = new Set(["bathroom", "hall"])

// houseDesign schema: v1 (Stage 9a) = floors with a wall graph only; v2
// (Stage 9b) adds detected rooms (name/type/polygon/area); v3 (Stage 9c) adds
// openings (windows/doors); v4 (Stage 14a) adds construction (wall thickness by
// kind + wall height) and classifies walls exterior/interior.
const HOUSE_SCHEMA_VERSION = 4

// Default constructive spec (Stage 14a). Exterior walls are load-bearing/thicker;
// interior partitions thinner. wallHeightM is a single storey height (per-floor
// heights arrive with Stage 9d). Drives the true-thickness render and the
// Stage 14d material estimate.
const DEFAULT_CONSTRUCTION = { exteriorThicknessM: 0.4, interiorThicknessM: 0.12, wallHeightM: 3 }
// Material-estimate defaults (Stage 14d): chosen materials, per-line price
// overrides, and foundation/roof dimensions. A factory — each design needs its
// OWN fresh objects so price/selection edits don't alias across houses.
function defaultEstimate() {
  return { selections: { ...DEFAULT_SELECTIONS }, prices: {}, params: { ...DEFAULT_PARAMS } }
}
const ESTIMATE_CATEGORIES = [
  { key: "wall", label: "Стены" },
  { key: "foundation", label: "Фундамент" },
  { key: "roof", label: "Кровля" },
  { key: "floor", label: "Перекрытие" },
]
const MIN_THICKNESS_M = 0.05
const MAX_THICKNESS_M = 1.5
const MIN_WALL_HEIGHT_M = 2
const MAX_WALL_HEIGHT_M = 10

// House floor-plan editor (Stage 9a). A full-screen overlay that edits a
// WORKING COPY of the selected house's wall-graph and commits it back to the
// store as a single undoable change on close. Opened via the `house:open`
// window event (double-click a house in the site editor, or the Properties
// "Планировка" button).
//
// Two tools:
//   room — drag a rectangle to add a room (welds onto existing walls)
//   edit — drag vertices to reshape; click a wall + Delete to remove it;
//          drag empty space to pan
// Wheel zooms at the cursor. The view auto-fits the plan after structural
// edits, so no manual pan is needed for small plans.

const SNAP_M = 0.1
const MIN_ROOM_M = 0.5
const MAX_UNDO = 50
const DEFAULT_FRAME = { minX: 0, minY: 0, widthM: 12, heightM: 9 }
// Cap the fit-zoom so a tiny first room doesn't balloon into giant slabs —
// walls would otherwise render metres-thick. ~70 px per metre keeps a 0.2 m
// wall around 14 px before the stroke clamp.
const MAX_PX_PER_M = 70
const WALL_MIN_PX = 1.5
const WALL_COLOR = "#334155" // slate-700 — exterior
const WALL_INTERIOR_COLOR = "#64748b" // slate-500 — interior partition
const SELECTED_COLOR = "#dc2626"
const VERTEX_COLOR = "#4338ca"
const GRID_MINOR = "#e5e7eb"
const GRID_MAJOR = "#cbd5e1"

export default class extends Controller {
  static targets = ["modal", "canvas", "readout", "hint", "roomTool", "editTool", "openingTool", "roomList", "openingList", "advisorType", "extThickness", "intThickness", "wallHeight", "estimateList", "lengthPopover", "lengthInput", "lengthAnchor", "lengthApply", "lengthError", "snapToggle"]
  // Read-only view (public shared link): open the overlay to pan/zoom and read
  // room labels, but suppress every edit affordance (Stage 15).
  static values = { readOnly: Boolean }

  connect() {
    // Explicit initial so guards reading this._readOnly before the first open()
    // see a real boolean (also re-read per-session in open()).
    this._readOnly = this.readOnlyValue
    this._onOpen = (e) => this.open(e.detail || {})
    window.addEventListener("house:open", this._onOpen)
    this._onKeyDown = (e) => this.#onKeyDown(e)
    // Dev-only test hook, mirroring editor_controller's window.__editor.
    if (import.meta.env.DEV) window.__houseMode = this
  }

  // Dev/test handle: read the live stage + metre↔pixel scale + working design.
  get debugHandle() {
    return { stage: this.stage, scale: this.scale, design: this.design, mode: this.mode, advisorType: this.advisorType, advisorSpotCount: this._advisorSpotCount }
  }

  disconnect() {
    window.removeEventListener("house:open", this._onOpen)
    this.#teardown()
  }

  open({ id } = {}) {
    if (!id) return
    const obj = editorStore.objects.find((o) => o.id === id)
    if (!obj || obj.type !== "house") return

    // Re-opening (e.g. double-click a different house while already open):
    // commit the in-progress session so its edits aren't silently lost, and
    // cancel any pending stage-build RAF so it can't fire after re-init.
    if (this.houseId && this.houseId !== id) this.#commit()
    if (this._raf) {
      cancelAnimationFrame(this._raf)
      this._raf = null
    }
    // A deferred insolation timer from the previous session would otherwise fire
    // after we've switched houses and kick off a redundant second heatmap.
    if (this._insolTimer) {
      clearTimeout(this._insolTimer)
      this._insolTimer = null
    }

    this.houseId = id
    this._readOnly = this.readOnlyValue
    const hadSavedPlan = !!obj.houseDesign
    this.design = this.#workingDesignFrom(obj.houseDesign, obj)
    // Derive rooms up front so the loaded JSON we diff against in #commit already
    // reflects detected geometry — a pure look-and-close then leaves it byte-for-
    // byte unchanged (no spurious persist / undo entry).
    this.#syncRooms()
    this._originalJson = hadSavedPlan ? JSON.stringify(this.design) : "null"
    // When the plan was auto-seeded from the house outline (no saved plan yet),
    // a look-and-close shouldn't persist or push an undo entry — remember the
    // seed so #commit can tell "user drew nothing" from "user edited".
    this._seededJson = hadSavedPlan ? null : JSON.stringify(this.design)
    this._undoStack = []
    this._redoStack = []
    this.mode = "room"
    this.selectedWallId = null
    this.selectedRoomIndex = null
    this.selectedOpeningId = null
    // Edit-mode drag snapping (ortho-to-neighbour + column/row alignment). On by
    // default; an editor preference, not part of the persisted design.
    this._snapEnabled = true
    // Coarse pointer (touch) → fatter vertex handles and label hit areas so a
    // fingertip can land them (Stage 14b.3).
    this._coarsePointer = typeof window.matchMedia === "function" && window.matchMedia("(pointer: coarse)").matches
    this.advisorType = null
    this._advisorSpotCount = 0
    this._drawStart = null
    this._drawEnd = null

    this.modalTarget.classList.remove("hidden")
    this.modalTarget.setAttribute("aria-hidden", "false")
    document.body.classList.add("house-mode-open")
    document.addEventListener("keydown", this._onKeyDown)

    // Defer stage init one frame so the canvas container has a real size.
    this._raf = requestAnimationFrame(() => {
      this._raf = null
      this.#buildStage()
      this.#fit()
      this.#render()
      this.#applyMode()
      this.#syncConstructionInputs()
      if (this.hasSnapToggleTarget) this.snapToggleTarget.checked = this._snapEnabled
      // Per-room insolation is a ~300 ms whole-plot heatmap — defer it past the
      // overlay's first paint so opening stays snappy, then refresh the panel.
      this._insolTimer = setTimeout(() => {
        this._insolTimer = null
        this.#computeInsolation()
        this.#renderRoomPanel()
        this.#renderAdvisor()
      }, 0)
    })
  }

  close() {
    if (!this.houseId) return
    // Drop any in-progress rubber-band so a half-drawn room can't leak in.
    this._drawStart = null
    this._drawEnd = null
    this.#closeLengthPopover()
    this.#commit()
    this.#teardown()
  }

  // ---- tool actions (wired from the overlay buttons) ----

  useRoomTool() {
    this.#closeLengthPopover()
    this.mode = "room"
    this.selectedWallId = null
    this.#applyMode()
    this.#render()
  }

  useEditTool() {
    this.#closeLengthPopover()
    this.mode = "edit"
    this.#applyMode()
    this.#render()
  }

  useOpeningTool() {
    this.#closeLengthPopover()
    this.mode = "opening"
    this.selectedWallId = null
    this.#applyMode()
    this.#render()
  }

  // Toggle edit-mode drag snapping (ortho/alignment). Read from the checkbox.
  toggleSnap() {
    this._snapEnabled = this.hasSnapToggleTarget ? this.snapToggleTarget.checked : !this._snapEnabled
  }

  // Advisor: arm a room type (from the "Советник" select) → highlight the best
  // spots in the footprint for that type. Empty value disarms.
  armAdvisor() {
    const v = this.hasAdvisorTypeTarget ? this.advisorTypeTarget.value : ""
    this.advisorType = v || null
    this.#renderAdvisor()
  }

  // Construction spec edited (exterior/interior wall thickness, wall height).
  // Re-derives every wall's thickness from the new spec and redraws. A setting,
  // not geometry — not pushed to the undo stack (persisted via the design JSON).
  updateConstruction() {
    const c = this.design.construction
    // Clamp into a sane range (so e.g. a fat-fingered 99 m wall becomes 1.5 m,
    // not a canvas-filling slab). The clamped value is echoed back to the input.
    const read = (target, min, max) => {
      if (!target) return null
      const v = parseFloat(target.value)
      if (!Number.isFinite(v)) return null
      return Math.min(max, Math.max(min, v))
    }
    const ext = read(this.hasExtThicknessTarget && this.extThicknessTarget, MIN_THICKNESS_M, MAX_THICKNESS_M)
    const int = read(this.hasIntThicknessTarget && this.intThicknessTarget, MIN_THICKNESS_M, MAX_THICKNESS_M)
    const h = read(this.hasWallHeightTarget && this.wallHeightTarget, MIN_WALL_HEIGHT_M, MAX_WALL_HEIGHT_M)
    if (ext != null) c.exteriorThicknessM = ext
    if (int != null) c.interiorThicknessM = int
    if (h != null) c.wallHeightM = h
    this.#syncRooms() // re-derive per-wall thickness from the spec
    this.#syncConstructionInputs()
    this.#render()
  }

  #syncConstructionInputs() {
    const c = this.design?.construction
    if (!c) return
    if (this.hasExtThicknessTarget) this.extThicknessTarget.value = String(c.exteriorThicknessM)
    if (this.hasIntThicknessTarget) this.intThicknessTarget.value = String(c.interiorThicknessM)
    if (this.hasWallHeightTarget) this.wallHeightTarget.value = String(c.wallHeightM)
  }

  undo() {
    const prev = this._undoStack.pop()
    if (!prev) return
    this.#closeLengthPopover()
    this._redoStack.push(this.#currentSnapshot())
    this.#restoreSnapshot(prev)
    this.#fit()
    this.#render()
  }

  redo() {
    const next = this._redoStack.pop()
    if (!next) return
    this.#closeLengthPopover()
    this._undoStack.push(this.#currentSnapshot())
    this.#restoreSnapshot(next)
    this.#fit()
    this.#render()
  }

  clear() {
    if (!this.#graph().walls.length) return
    if (!window.confirm("Очистить всю планировку? Это можно отменить кнопкой «Отменить».")) return
    this.#closeLengthPopover()
    this.#pushUndo()
    this.#setGraph(createGraph())
    this.selectedWallId = null
    this.selectedOpeningId = null
    this.#fit()
    this.#render()
  }

  // ---- working state ----

  #graph() {
    return this.design.floors[0].graph
  }

  #setGraph(g) {
    const floor = this.design.floors[0]
    // Re-anchor openings against the new graph BEFORE swapping it in: walls may
    // have been split/renamed by normalizeGraph, so each opening must re-attach
    // to whichever wall now passes through it (or be dropped if its wall is gone).
    floor.openings = reconcileOpenings(g, floor.graph, floor.openings ?? [])
    floor.graph = g
    // Rooms are derived from the graph: re-detect after every structural change,
    // carrying the user's name/type onto whichever room still holds its centroid.
    this.#syncRooms()
    // Room order (by area) can shift after a structural edit, so an index-based
    // selection is no longer meaningful — drop it.
    this.selectedRoomIndex = null
  }

  #rooms() {
    return this.design.floors[0].rooms ?? []
  }

  #openings() {
    return this.design.floors[0].openings ?? []
  }

  // The single plan→plot anchor (graph bbox min) shared by norms, per-room sun,
  // the advisor and the site indicator. Falls back to the footprint min for
  // designs saved before planOrigin existed (single-component, where they
  // coincide), then to the origin.
  #planAnchor() {
    const floor = this.design.floors[0]
    if (Array.isArray(floor.planOrigin)) return floor.planOrigin
    if (Array.isArray(floor.footprint) && floor.footprint.length) return polygonMin(floor.footprint)
    return [0, 0]
  }

  // Internal floor-plan norm violations (room area, corridor width) grouped by
  // room index. Recomputed on demand — pure and cheap for a few rooms.
  #floorViolationsByRoom() {
    const ruleSet = loadFloorplanRuleSet()
    return violationsByRoomIndex(validateFloor({ rooms: this.#rooms(), ruleSet }))
  }

  #setOpenings(openings) {
    this.design.floors[0].openings = openings
    this.design.schemaVersion = HOUSE_SCHEMA_VERSION
  }

  // Re-derive the floor's rooms from its graph, preserving name/type metadata
  // across the edit, and bump the schema version to v2 (rooms present).
  #syncRooms() {
    const floor = this.design.floors[0]
    if (!this.design.construction) this.design.construction = { ...DEFAULT_CONSTRUCTION }
    if (!this.design.estimate) this.design.estimate = defaultEstimate()
    floor.rooms = reconcileRooms(floor.graph, floor.rooms ?? [])
    if (!Array.isArray(floor.openings)) floor.openings = []
    // Classify walls exterior/interior and set each wall's thickness from the
    // construction spec (Stage 14a). Derived on every structural change so the
    // render and the material estimate always reflect the current topology.
    const kinds = classifyWalls(floor.graph)
    const c = this.design.construction
    for (const w of floor.graph.walls) {
      w.kind = kinds.get(w.id) ?? "exterior"
      w.thicknessM = w.kind === "interior" ? c.interiorThicknessM : c.exteriorThicknessM
    }
    // Real outer contour (concave footprint) for the site norms validator and
    // the plan indicator — replaces the bbox-rect approximation of Stage 9a.
    floor.footprint = outerContour(floor.graph)
    // The plan→plot anchor: the graph's bounding-box min maps to the object's
    // (xM, yM). This is the SINGLE source of truth shared by the site plan
    // indicator, objectFootprintPolygon (norms) and per-room insolation — it
    // must be the graph bbox min, NOT the footprint min, because for a
    // disconnected plan outerContour() returns only the largest component while
    // the object rect (and indicator) span the whole graph.
    const bb = boundingBox(floor.graph)
    floor.planOrigin = [bb.minX, bb.minY]
    this.design.schemaVersion = HOUSE_SCHEMA_VERSION
  }

  // A snapshot holds BOTH the graph and the openings — openings live on the
  // floor (not in the graph), so a graph-only snapshot would leave a placed/
  // deleted opening un-undoable.
  #currentSnapshot() {
    return {
      graph: cloneGraph(this.#graph()),
      openings: this.#openings().map((o) => ({ ...o })),
    }
  }

  #pushUndo() {
    this._undoStack.push(this.#currentSnapshot())
    this._redoStack = [] // a new edit invalidates the redo branch
    if (this._undoStack.length > MAX_UNDO) this._undoStack.shift()
  }

  #restoreSnapshot(snap) {
    const floor = this.design.floors[0]
    // Clone so #syncRooms (which writes kind/thicknessM onto walls) can't mutate
    // the snapshot record — keeps history immutable, matching the site store.
    floor.graph = cloneGraph(snap.graph)
    floor.openings = snap.openings.map((o) => ({ ...o }))
    this.#syncRooms()
    this.selectedRoomIndex = null
    this.selectedOpeningId = null
    this.selectedWallId = null
  }

  #workingDesignFrom(hd, obj) {
    if (hd && Array.isArray(hd.floors) && hd.floors.length) {
      return {
        schemaVersion: hd.schemaVersion ?? 1,
        construction: { ...DEFAULT_CONSTRUCTION, ...(hd.construction ?? {}) },
        estimate: {
          selections: { ...DEFAULT_SELECTIONS, ...(hd.estimate?.selections ?? {}) },
          prices: { ...(hd.estimate?.prices ?? {}) },
          params: { ...DEFAULT_PARAMS, ...(hd.estimate?.params ?? {}) },
        },
        floors: hd.floors.map((f) => ({
          level: f.level ?? 0,
          graph: cloneGraph(f.graph),
          // Carry saved room name/type metadata; geometry is re-derived in #syncRooms.
          rooms: Array.isArray(f.rooms) ? f.rooms.map((r) => ({ ...r })) : [],
          // Carry saved openings; geometry is re-anchored in #syncRooms.
          openings: Array.isArray(f.openings) ? f.openings.map((o) => ({ ...o })) : [],
        })),
      }
    }
    // No saved plan yet: seed the house's outer rectangle as exterior walls so
    // the user immediately sees the house boundary and draws rooms inside it.
    const widthM = obj?.widthM > 0 ? obj.widthM : DEFAULT_FRAME.widthM
    const heightM = obj?.heightM > 0 ? obj.heightM : DEFAULT_FRAME.heightM
    const graph = addRoomRect(createGraph(), { xM: 0, yM: 0, widthM, heightM })
    return {
      schemaVersion: 1,
      construction: { ...DEFAULT_CONSTRUCTION },
      estimate: defaultEstimate(),
      floors: [{ level: 0, graph }],
    }
  }

  #commit() {
    // A read-only viewer must never write back to the store (belt-and-suspenders:
    // the design isn't mutated in read-only, so the diff below would already
    // no-op, but this makes the contract explicit and future-proof).
    if (this._readOnly) return
    const nextJson = JSON.stringify(this.design)
    if (nextJson === this._originalJson) return
    // Fresh house (no saved plan) that the user only looked at — the working
    // design is still the bare auto-seeded outline (or was cleared to empty).
    // Don't persist it or push an undo entry.
    if (this._originalJson === "null" &&
        (this.#graph().walls.length === 0 || nextJson === this._seededJson)) return
    // The house IS the plan: resize the site rect to the plan's bounding box so
    // a plan drawn bigger/smaller than the seeded outline grows/shrinks the
    // house instead of being squished into a mismatched rect. Anchored at the
    // rect's top-left; rotation is preserved.
    const patch = { houseDesign: this.design }
    const bb = boundingBox(this.#graph())
    if (bb.widthM > 0 && bb.heightM > 0) {
      patch.widthM = Math.round(bb.widthM * 10) / 10
      patch.heightM = Math.round(bb.heightM * 10) / 10
    }
    // One snapshot for the whole editing session, so a single undo in the site
    // editor reverts the entire plan change.
    editorStore.updateObject(this.houseId, patch, { snapshot: true })
  }

  // ---- Konva stage ----

  #buildStage() {
    this.#destroyStage()
    const el = this.canvasTarget
    this.stage = new Konva.Stage({ container: el, width: el.clientWidth, height: el.clientHeight })
    this.gridLayer = new Konva.Layer({ listening: false })
    this.roomLayer = new Konva.Layer({ listening: false })
    this.advisorLayer = new Konva.Layer({ listening: false })
    this.wallLayer = new Konva.Layer()
    this.openingLayer = new Konva.Layer()
    this.labelLayer = new Konva.Layer({ listening: false })
    this.vertexLayer = new Konva.Layer()
    this.previewLayer = new Konva.Layer({ listening: false })
    this.stage.add(this.gridLayer, this.roomLayer, this.advisorLayer, this.wallLayer, this.openingLayer, this.labelLayer, this.vertexLayer, this.previewLayer)

    this.stage.on("mousedown touchstart", (e) => this.#onPointerDown(e))
    this.stage.on("mousemove touchmove", () => this.#onPointerMove())
    this.stage.on("mouseup touchend", () => this.#onPointerUp())

    this._onWheel = (ev) => this.#onWheel(ev)
    el.addEventListener("wheel", this._onWheel, { passive: false })

    this._ro = new ResizeObserver(() => {
      if (!this.stage) return
      this.stage.size({ width: el.clientWidth, height: el.clientHeight })
      this.#fit()
      this.#render()
    })
    this._ro.observe(el)
  }

  #destroyStage() {
    this._ro?.disconnect()
    this._ro = null
    if (this._onWheel) {
      this.canvasTarget?.removeEventListener("wheel", this._onWheel)
      this._onWheel = null
    }
    this.stage?.destroy()
    this.stage = null
  }

  #applyMode() {
    // Edit mode (or read-only view) lets dragging empty space pan the stage.
    if (this.stage) this.stage.draggable(this.mode === "edit" || this._readOnly)
    this.#styleToolButton(this.hasRoomToolTarget && this.roomToolTarget, this.mode === "room")
    this.#styleToolButton(this.hasEditToolTarget && this.editToolTarget, this.mode === "edit")
    this.#styleToolButton(this.hasOpeningToolTarget && this.openingToolTarget, this.mode === "opening")
    if (this.hasHintTarget) {
      const hints = {
        room: "Проведите прямоугольник, чтобы добавить комнату. Смежные стены свариваются.",
        edit: "Тяните узлы (привязка к осям соседей). Длина стены: тап по размеру, двойной клик по стене или Enter. Клик по стене + Delete — удалить.",
        opening: "Клик по стене ставит окно. Клик по проёму выделяет; Delete — удалить.",
      }
      this.hintTarget.textContent = this._readOnly
        ? "Только просмотр. Колесо мыши — масштаб, перетаскивание — сдвиг."
        : (hints[this.mode] ?? hints.room)
    }
  }

  #styleToolButton(btn, active) {
    if (!btn) return
    btn.classList.toggle("bg-indigo-600", active)
    btn.classList.toggle("text-white", active)
    btn.classList.toggle("bg-white", !active)
    btn.classList.toggle("text-neutral-700", !active)
  }

  #fit() {
    if (!this.stage) return
    const bb = boundingBox(this.#graph())
    const base = bb.widthM > 0.01 && bb.heightM > 0.01
      ? { minX: bb.minX, minY: bb.minY, widthM: bb.widthM, heightM: bb.heightM }
      : DEFAULT_FRAME
    const pad = 0.25
    const frame = {
      minX: base.minX - (base.widthM * pad) / 2,
      minY: base.minY - (base.heightM * pad) / 2,
      widthM: base.widthM * (1 + pad),
      heightM: base.heightM * (1 + pad),
    }
    const w = this.stage.width()
    const h = this.stage.height()
    const margin = 28
    // Clamp the zoom: a small plan should sit in the middle of a grid, not
    // fill the canvas with metres-thick walls.
    const ppm = Math.min(
      (w - margin * 2) / frame.widthM,
      (h - margin * 2) / frame.heightM,
      MAX_PX_PER_M,
    )
    const offsetPx = {
      x: (w - frame.widthM * ppm) / 2 - frame.minX * ppm,
      y: (h - frame.heightM * ppm) / 2 - frame.minY * ppm,
    }
    this.scale = { pxPerMetre: ppm, offsetPx }
    // Content bounds (unpadded) drive the grid extent.
    this._frame = { minX: base.minX, minY: base.minY, maxX: base.minX + base.widthM, maxY: base.minY + base.heightM }
    this.stage.scale({ x: 1, y: 1 })
    this.stage.position({ x: 0, y: 0 })
  }

  #pointerM() {
    const p = this.stage?.getRelativePointerPosition()
    if (!p) return null
    return pxToMetres(p, this.scale)
  }

  // Magnet a drawing point onto an existing vertex (so adjacent rooms align and
  // weld), else snap to the 0.1 m grid. Tolerance is ~10 px or 0.3 m, whichever
  // is larger, so the snap feels right at any zoom.
  #snapPoint(m) {
    const tol = Math.max(0.3, 10 / (this.scale?.pxPerMetre || 1))
    let best = null
    let bestD = tol
    for (const v of this.#graph().vertices) {
      const d = Math.hypot(v.xM - m.xM, v.yM - m.yM)
      if (d < bestD) { bestD = d; best = v }
    }
    if (best) return { xM: best.xM, yM: best.yM }
    return { xM: snapM(m.xM), yM: snapM(m.yM) }
  }

  #onPointerDown(e) {
    if (this._readOnly) return // read-only viewer: pan/zoom only, never edits
    if (this.mode === "room") {
      const m = this.#pointerM()
      if (!m) return
      this._drawStart = this.#snapPoint(m)
      this._drawEnd = this._drawStart
    } else if (this.mode === "opening") {
      const openingId = e.target?.getAttr?.("openingId")
      if (openingId) {
        this.selectedOpeningId = openingId
        this.#render()
        return
      }
      const m = this.#pointerM()
      if (!m) return
      // Generous click radius (≥0.6 m or ~14 px) so a tap near a wall lands one.
      const tol = Math.max(0.6, 14 / (this.scale?.pxPerMetre || 1))
      const prev = this.#openings()
      const next = placeOpening(this.#graph(), prev, m.xM, m.yM, "window", tol)
      if (next === prev) return // no wall within reach → nothing placed
      this.#pushUndo()
      this.#setOpenings(next)
      this.selectedOpeningId = next[next.length - 1].id
      this.#render()
    } else {
      const wallId = e.target?.getAttr?.("wallId")
      if (wallId) {
        this.selectedWallId = wallId
        this.#render()
      } else if (e.target === this.stage) {
        // Clicking empty space dismisses an open length popover (tapping a pill
        // re-fires its own click after this, so re-anchoring still works).
        const hadPopover = this.#lengthPopoverOpen()
        if (hadPopover) this.#closeLengthPopover()
        if (this.selectedWallId) {
          this.selectedWallId = null
          this.#render()
        } else if (hadPopover) {
          this.#renderWallLabels() // drop the edited-pill highlight
        }
      }
    }
  }

  #onPointerMove() {
    if (this.mode !== "room" || !this._drawStart) return
    const m = this.#pointerM()
    if (!m) return
    this._drawEnd = this.#snapPoint(m)
    this.#renderPreview(this._drawStart, this._drawEnd)
  }

  #onPointerUp() {
    if (this.mode !== "room" || !this._drawStart) return
    const start = this._drawStart
    const end = this._drawEnd ?? start
    this._drawStart = null
    this._drawEnd = null
    this.previewLayer.destroyChildren()
    this.previewLayer.batchDraw()

    const x = Math.min(start.xM, end.xM)
    const y = Math.min(start.yM, end.yM)
    const wM = Math.abs(end.xM - start.xM)
    const hM = Math.abs(end.yM - start.yM)
    if (wM < MIN_ROOM_M || hM < MIN_ROOM_M) return

    this.#pushUndo()
    this.#setGraph(addRoomRect(this.#graph(), { xM: x, yM: y, widthM: wM, heightM: hM }))
    this.#fit()
    this.#render()
  }

  #onWheel(ev) {
    ev.preventDefault()
    if (!this.stage) return
    const old = this.stage.scaleX()
    const pointer = this.stage.getPointerPosition()
    if (!pointer) return
    const to = { x: (pointer.x - this.stage.x()) / old, y: (pointer.y - this.stage.y()) / old }
    const factor = Math.exp(-ev.deltaY * 0.0015)
    const ns = Math.min(8, Math.max(0.2, old * factor))
    if (ns === old) return
    this.stage.scale({ x: ns, y: ns })
    this.stage.position({ x: pointer.x - to.x * ns, y: pointer.y - to.y * ns })
    this.stage.batchDraw()
  }

  #onKeyDown(e) {
    if (!this.houseId) return
    // Read-only viewer: only Escape (close); no undo/redo/Delete/Enter edits.
    if (this._readOnly) {
      if (e.key === "Escape") this.close()
      return
    }
    if (e.key === "Escape") {
      // A nested length popover swallows Escape — close it without tearing the
      // whole overlay down.
      if (this.#lengthPopoverOpen()) { this.#closeLengthPopover(); return }
      this.close()
      return
    }
    // Undo / redo (Ctrl/Cmd+Z, Ctrl/Cmd+Shift+Z or Ctrl+Y).
    if ((e.metaKey || e.ctrlKey) && (e.key === "z" || e.key === "Z" || e.key === "y" || e.key === "Y")) {
      e.preventDefault()
      if ((e.key === "y" || e.key === "Y") || e.shiftKey) this.redo()
      else this.undo()
      return
    }
    if ((e.key === "Delete" || e.key === "Backspace") && this.mode === "edit" && this.selectedWallId) {
      e.preventDefault()
      this.#pushUndo()
      this.#setGraph(removeWall(this.#graph(), this.selectedWallId))
      this.selectedWallId = null
      this.#render()
    }
    // Enter on a selected wall (edit mode) opens its length editor — an
    // alternative to hitting the small midpoint pill (Stage 14b F4).
    if (e.key === "Enter" && this.mode === "edit" && this.selectedWallId && !this.#lengthPopoverOpen()) {
      e.preventDefault()
      this.#openLengthPopover(this.selectedWallId)
    }
    if ((e.key === "Delete" || e.key === "Backspace") && this.mode === "opening" && this.selectedOpeningId) {
      e.preventDefault()
      this.#pushUndo()
      this.#setOpenings(removeOpening(this.#openings(), this.selectedOpeningId))
      this.selectedOpeningId = null
      this.#render()
    }
  }

  // ---- rendering ----

  #render() {
    if (!this.stage) return
    const g = this.#graph()
    const byId = new Map(g.vertices.map((v) => [v.id, v]))

    // Compute internal-norm violations once per render and share with both the
    // canvas outline pass and the side-panel badge pass.
    const violByRoom = this.#floorViolationsByRoom()
    this.#renderGrid()
    this.#renderRooms(violByRoom)
    this.#renderAdvisor()

    this.wallLayer.destroyChildren()
    for (const w of g.walls) {
      const a = byId.get(w.a)
      const b = byId.get(w.b)
      if (!a || !b) continue
      const pa = metresToPx({ xM: a.xM, yM: a.yM }, this.scale)
      const pb = metresToPx({ xM: b.xM, yM: b.yM }, this.scale)
      // True-to-scale thickness (Stage 14a): a wall is drawn at its real width,
      // never thinner than 1.5 px so it stays visible when zoomed out.
      const sw = Math.max(WALL_MIN_PX, (w.thicknessM ?? 0.2) * this.scale.pxPerMetre)
      const baseColor = w.kind === "interior" ? WALL_INTERIOR_COLOR : WALL_COLOR
      const line = new Konva.Line({
        points: [pa.x, pa.y, pb.x, pb.y],
        stroke: w.id === this.selectedWallId ? SELECTED_COLOR : baseColor,
        strokeWidth: sw,
        lineCap: "round",
        lineJoin: "round",
        hitStrokeWidth: Math.max(14, sw),
      })
      line.setAttr("wallId", w.id)
      // Double-click/tap a wall (edit mode) → edit its length directly, without
      // having to hit the small midpoint pill (Stage 14b F4).
      line.on("dblclick dbltap", (e) => {
        if (this._readOnly || this.mode !== "edit") return
        e.cancelBubble = true
        this.#openLengthPopover(w.id)
      })
      this.wallLayer.add(line)
    }
    this.wallLayer.batchDraw()
    this.#renderOpenings()
    this.#renderWallLabels()

    this.vertexLayer.destroyChildren()
    if (this.mode === "edit" && !this._readOnly) {
      const vr = this._coarsePointer ? 11 : 7
      for (const v of g.vertices) {
        const p = metresToPx({ xM: v.xM, yM: v.yM }, this.scale)
        const dot = new Konva.Circle({
          x: p.x,
          y: p.y,
          radius: vr,
          fill: "#ffffff",
          stroke: VERTEX_COLOR,
          strokeWidth: 2,
          // Widen the touch hit area beyond the visible dot on coarse pointers.
          hitStrokeWidth: this._coarsePointer ? 16 : 4,
          draggable: true,
        })
        dot.setAttr("vertexId", v.id)
        dot.on("dragstart", () => { this.#closeLengthPopover(); this.#pushUndo() })
        dot.on("dragmove", () => this.#onVertexDragMove(v.id, dot))
        dot.on("dragend", () => this.#onVertexDragEnd(v.id, dot))
        this.vertexLayer.add(dot)
      }
    }
    this.vertexLayer.batchDraw()
    this.#renderRoomPanel(violByRoom)
    this.#renderOpeningPanel()
    this.#renderEstimate()
    this.#updateReadout()
  }

  // Windows and doors drawn over the walls: a white gap masks the wall, then a
  // symbol marks the kind — a cyan pane line for a window, a swing arc for a
  // door. A wide transparent hit line on top makes the whole opening tappable
  // for selection. Only listens in opening mode.
  #renderOpenings() {
    if (!this.openingLayer) return
    this.openingLayer.destroyChildren()
    this.openingLayer.listening(this.mode === "opening")
    if (!this.scale) { this.openingLayer.batchDraw(); return }
    const g = this.#graph()
    const footprint = this.design.floors[0].footprint
    const inward = Array.isArray(footprint) && footprint.length >= 3 ? polygonCentroid(footprint) : null
    const ppm = this.scale.pxPerMetre
    const wallById = new Map(g.walls.map((w) => [w.id, w]))
    for (const op of this.#openings()) {
      const seg = openingSegment(g, op)
      if (!seg) continue
      const pa = metresToPx({ xM: seg.a[0], yM: seg.a[1] }, this.scale)
      const pb = metresToPx({ xM: seg.b[0], yM: seg.b[1] }, this.scale)
      const selected = op.id === this.selectedOpeningId
      // Gap erases the wall beneath — size it to the wall's real thickness.
      const wallThick = wallById.get(op.wallId)?.thicknessM ??
        this.design.construction?.exteriorThicknessM ?? DEFAULT_CONSTRUCTION.exteriorThicknessM
      const gapPx = Math.max(WALL_MIN_PX, wallThick * ppm) + 3
      // White gap erases the wall beneath the opening.
      this.openingLayer.add(new Konva.Line({
        points: [pa.x, pa.y, pb.x, pb.y], stroke: "#ffffff", strokeWidth: gapPx, lineCap: "butt", listening: false,
      }))
      if (op.kind === "window") {
        this.openingLayer.add(new Konva.Line({
          points: [pa.x, pa.y, pb.x, pb.y],
          stroke: selected ? SELECTED_COLOR : "#0ea5e9", // sky-500
          strokeWidth: selected ? 4 : 2.5,
          lineCap: "round",
          listening: false,
        }))
      } else {
        this.#renderDoor(seg, pa, pb, ppm, inward, selected)
      }
      // Transparent fat hit line for selection.
      const hit = new Konva.Line({
        points: [pa.x, pa.y, pb.x, pb.y], stroke: "rgba(0,0,0,0.001)", strokeWidth: 18, lineCap: "round",
      })
      hit.setAttr("openingId", op.id)
      this.openingLayer.add(hit)
    }
    this.openingLayer.batchDraw()
  }

  // A door: the threshold line plus a quarter-circle swing arc hinged at one
  // jamb, opening toward the house interior (footprint centroid side).
  #renderDoor(seg, pa, pb, ppm, inward, selected) {
    const color = selected ? SELECTED_COLOR : "#92400e" // amber-800
    this.openingLayer.add(new Konva.Line({
      points: [pa.x, pa.y, pb.x, pb.y], stroke: color, strokeWidth: selected ? 3 : 2, lineCap: "round", listening: false,
    }))
    // Swing side: the wall normal pointing toward the interior. The default
    // (−uy, ux) is the wall direction rotated +90° in screen coords; if we flip
    // it to face the interior, the arc must sweep the other way too.
    let nx = -seg.uy, ny = seg.ux
    let sweep = 90
    if (inward) {
      const dx = inward[0] - seg.center[0], dy = inward[1] - seg.center[1]
      if (nx * dx + ny * dy < 0) { nx = -nx; ny = -ny; sweep = -90 }
    }
    const rPx = seg.widthM * ppm
    // Arc from jamb `a`, sweeping from along-wall toward the inward normal.
    const baseRad = Math.atan2(pb.y - pa.y, pb.x - pa.x)
    const leafEnd = { x: pa.x + nx * rPx, y: pa.y + ny * rPx }
    this.openingLayer.add(new Konva.Line({
      points: [pa.x, pa.y, leafEnd.x, leafEnd.y], stroke: color, strokeWidth: 1.5, listening: false,
    }))
    this.openingLayer.add(new Konva.Arc({
      x: pa.x, y: pa.y, innerRadius: rPx, outerRadius: rPx,
      angle: sweep, rotation: (baseRad * 180) / Math.PI,
      stroke: color, strokeWidth: 1, dash: [4, 3], listening: false,
    }))
  }

  // Translucent fill + centroid label (name + area) for each detected room.
  // Non-listening, so it never intercepts drawing, panning or wall selection.
  // `violByRoom` is shared from #render(); standalone callers let it default.
  #renderRooms(violByRoom = this.#floorViolationsByRoom()) {
    this.roomLayer.destroyChildren()
    if (!this.scale) { this.roomLayer.batchDraw(); return }
    const rooms = this.#rooms()
    rooms.forEach((room, i) => {
      const pts = room.polygon.flatMap(([xM, yM]) => {
        const p = metresToPx({ xM, yM }, this.scale)
        return [p.x, p.y]
      })
      const selected = i === this.selectedRoomIndex
      const violated = violByRoom.has(i)
      this.roomLayer.add(new Konva.Line({
        points: pts,
        closed: true,
        fill: roomColor(room),
        opacity: selected ? 0.6 : 0.35,
        // Amber dashed outline flags a room that breaks an internal norm
        // (too small / corridor too narrow); selection highlight takes priority.
        stroke: selected ? "#4338ca" : violated ? "#f59e0b" : "transparent",
        strokeWidth: selected ? 2 : violated ? 2 : 0,
        dash: !selected && violated ? [6, 4] : undefined,
        listening: false,
      }))
      // Label only rooms big enough to read (avoid clutter on slivers).
      if (room.areaM2 >= 1) {
        const c = metresToPx({ xM: room.centroid[0], yM: room.centroid[1] }, this.scale)
        const text = new Konva.Text({
          // Lead with the room's index so it maps 1:1 onto its side-panel row —
          // the panel is area-sorted and otherwise gives no spatial cue (Stage 14b).
          text: `№${i + 1} ${roomLabel(room)}\n${fmt(room.areaM2)} м²`,
          fontSize: 12,
          fontFamily: "Nunito, sans-serif",
          fontStyle: "600",
          fill: "#1f2937",
          align: "center",
          lineHeight: 1.25,
        })
        text.offsetX(text.width() / 2)
        text.offsetY(text.height() / 2)
        text.position({ x: c.x, y: c.y })
        text.listening(false)
        this.roomLayer.add(text)
      }
    })
    this.roomLayer.batchDraw()
  }

  // Side panel: one row per room with an editable name, a type selector and the
  // computed area. Built with DOM APIs (never innerHTML) so user names can't
  // inject markup. Clicking a row selects the room (highlights it on canvas).
  // `violByRoom` is shared from #render(); standalone callers let it default.
  #renderRoomPanel(violByRoom = this.#floorViolationsByRoom()) {
    if (!this.hasRoomListTarget) return
    const list = this.roomListTarget
    list.replaceChildren()
    const rooms = this.#rooms()
    if (!rooms.length) {
      const empty = document.createElement("p")
      empty.className = "px-3 py-3 text-xs text-neutral-500"
      empty.textContent = "Замкните стены, чтобы появились комнаты."
      list.appendChild(empty)
      return
    }
    if (!editorStore.plot?.originLngLat) {
      const note = document.createElement("p")
      note.className = "px-3 py-2 text-xs text-neutral-400"
      note.textContent = "Солнце по комнатам появится после загрузки участка на карте."
      list.appendChild(note)
    }
    let selectedRow = null
    rooms.forEach((room, i) => {
      const row = document.createElement("div")
      row.className = "px-3 py-2 cursor-pointer hover:bg-neutral-50"
      if (i === this.selectedRoomIndex) { row.classList.add("bg-indigo-50"); selectedRow = row }
      row.addEventListener("click", (e) => {
        if (e.target.closest("input, select")) return
        this.selectedRoomIndex = this.selectedRoomIndex === i ? null : i
        this.#renderRooms()
        this.#renderRoomPanel()
      })

      const top = document.createElement("div")
      top.className = "flex items-center gap-2"

      // Index badge — matches the "№N" prefix on the room's canvas label, so the
      // user can find a given room's row in the area-sorted list.
      const idx = document.createElement("span")
      idx.className = "text-xs font-semibold text-neutral-400 tabular-nums shrink-0 w-6"
      idx.textContent = `№${i + 1}`
      top.appendChild(idx)

      const swatch = document.createElement("span")
      swatch.className = "inline-block w-3 h-3 rounded-sm shrink-0"
      swatch.style.backgroundColor = roomColor(room)
      top.appendChild(swatch)

      if (this._readOnly) {
        // Read-only viewer: the room type is a static label, not a <select>.
        const typeLabel = document.createElement("span")
        typeLabel.className = "flex-1 min-w-0 text-xs text-neutral-700 truncate"
        typeLabel.textContent = ROOM_TYPES.find((t) => t.key === room.type)?.label ?? room.type
        top.appendChild(typeLabel)
      } else {
        const select = document.createElement("select")
        select.className = "flex-1 min-w-0 text-xs border border-neutral-300 rounded px-1 py-1 bg-white"
        select.setAttribute("aria-label", "Тип комнаты")
        for (const t of ROOM_TYPES) {
          const opt = document.createElement("option")
          opt.value = t.key
          opt.textContent = t.label
          if (t.key === room.type) opt.selected = true
          select.appendChild(opt)
        }
        select.addEventListener("change", () => {
          room.type = select.value
          this.#renderRooms()
          this.#renderRoomPanel()
        })
        top.appendChild(select)
      }

      const area = document.createElement("span")
      area.className = "text-xs text-neutral-500 tabular-nums shrink-0"
      area.textContent = `${fmt(room.areaM2)} м²`
      top.appendChild(area)
      row.appendChild(top)

      if (this._readOnly) {
        // Read-only viewer: the name is static text (the typed name, else the
        // default type label that the canvas shows).
        const nameEl = document.createElement("div")
        nameEl.className = "mt-1 w-full text-xs text-neutral-600"
        nameEl.textContent = room.name?.trim() ? room.name : roomLabel({ ...room, name: "" })
        row.appendChild(nameEl)
      } else {
        const name = document.createElement("input")
        name.type = "text"
        name.className = "mt-1 w-full text-xs border border-neutral-200 rounded px-1.5 py-1"
        name.placeholder = roomLabel({ ...room, name: "" })
        name.value = room.name ?? ""
        name.setAttribute("aria-label", "Название комнаты")
        name.addEventListener("input", () => {
          room.name = name.value
          this.#renderRooms() // refresh the canvas label live, but keep input focus
        })
        row.appendChild(name)
      }

      const sun = this.#roomSun(room)
      if (sun && sun.hasWindows === false && !NO_WINDOW_OK_TYPES.has(room.type)) {
        // Flag a windowless habitable/untyped room; skip the note for types
        // where no exterior window is normal (bathroom/corridor).
        const badge = document.createElement("div")
        badge.className = "mt-1 text-xs text-amber-600"
        badge.textContent = "🪟 нет окон наружу"
        badge.title = "Жилой комнате нужно окно на внешней стене для прямого солнца. Добавьте окно инструментом «Проём»."
        row.appendChild(badge)
      } else if (sun && sun.hasWindows !== false && sun.level !== "none") {
        const badge = document.createElement("div")
        const b = INSOLATION_BADGE[sun.level]
        badge.className = `mt-1 text-xs ${b.cls}`
        badge.textContent = `${b.dot} ${fmt(sun.sunHours)} ч прямого солнца`
        badge.title = "Прямое солнце с учётом ориентации окон комнаты на выбранную дату"
        row.appendChild(badge)
      }

      for (const v of violByRoom.get(i) ?? []) {
        const warn = document.createElement("div")
        warn.className = "mt-1 text-xs text-amber-600"
        warn.textContent = `⚠ ${v.title}`
        warn.title = `${v.message} (${v.normRef})`
        row.appendChild(warn)
      }

      const fit = this.#roomFit(room)
      if (fit) {
        const f = document.createElement("div")
        f.className = `mt-1 text-xs ${fit.good ? "text-emerald-600" : "text-amber-600"}`
        f.textContent = `${fit.good ? "👍" : "💡"} ${fit.label}`
        f.title = fit.good
          ? "Расположение комнаты подходит её типу по времени солнца"
          : "По солнцу этот тип комнаты обычно лучше разместить иначе — см. рекомендацию"
        row.appendChild(f)
      }

      list.appendChild(row)
    })
    // Bring the selected room's row into view (no-op when already visible) so a
    // canvas selection in a long list isn't lost below the fold.
    if (selectedRow) selectedRow.scrollIntoView({ block: "nearest" })
  }

  // Side panel section: one row per opening with a kind selector (window/door),
  // an editable width and a delete button. Built with DOM APIs (no innerHTML).
  // Clicking a row selects the opening (highlights it on canvas).
  #renderOpeningPanel() {
    if (!this.hasOpeningListTarget) return
    if (this._readOnly) return // edit-only panel — viewer sees openings on the canvas, not editable rows
    const list = this.openingListTarget
    list.replaceChildren()
    const openings = this.#openings()
    if (!openings.length) {
      const empty = document.createElement("p")
      empty.className = "px-3 py-3 text-xs text-neutral-500"
      empty.textContent = this.#graph().walls.length
        ? "Инструмент «Проём» → клик по стене ставит окно."
        : "Сначала нарисуйте стены."
      list.appendChild(empty)
      return
    }
    openings.forEach((op) => {
      const row = document.createElement("div")
      row.className = "px-3 py-2 cursor-pointer hover:bg-neutral-50 flex items-center gap-2"
      if (op.id === this.selectedOpeningId) row.classList.add("bg-indigo-50")
      row.addEventListener("click", (e) => {
        if (e.target.closest("input, select, button")) return
        this.selectedOpeningId = this.selectedOpeningId === op.id ? null : op.id
        this.#renderOpenings()
        this.#renderOpeningPanel()
      })

      const swatch = document.createElement("span")
      swatch.className = "inline-block w-3 h-3 rounded-sm shrink-0"
      swatch.style.backgroundColor = op.kind === "window" ? "#0ea5e9" : "#92400e"
      row.appendChild(swatch)

      const select = document.createElement("select")
      select.className = "text-xs border border-neutral-300 rounded px-1 py-1 bg-white"
      select.setAttribute("aria-label", "Тип проёма")
      for (const k of OPENING_KINDS) {
        const opt = document.createElement("option")
        opt.value = k.key
        opt.textContent = k.label
        if (k.key === op.kind) opt.selected = true
        select.appendChild(opt)
      }
      select.addEventListener("change", () => {
        this.#pushUndo()
        this.#setOpenings(updateOpening(this.#openings(), op.id, { kind: select.value }))
        this.#renderOpenings()
        this.#renderOpeningPanel()
      })
      row.appendChild(select)

      const width = document.createElement("input")
      width.type = "number"
      width.min = "0.4"
      width.step = "0.1"
      width.value = String(Math.round(op.widthM * 10) / 10)
      width.className = "w-16 text-xs border border-neutral-200 rounded px-1.5 py-1 tabular-nums"
      width.setAttribute("aria-label", "Ширина проёма, м")
      width.addEventListener("change", () => {
        const v = parseFloat(width.value)
        if (Number.isFinite(v) && v >= 0.4) {
          this.#pushUndo()
          this.#setOpenings(updateOpening(this.#openings(), op.id, { widthM: v }))
          // Redraw only the canvas — re-rendering the panel here would
          // replaceChildren() the very input that is firing its blur/change.
          this.#renderOpenings()
        }
      })
      row.appendChild(width)

      const unit = document.createElement("span")
      unit.className = "text-xs text-neutral-400"
      unit.textContent = "м"
      row.appendChild(unit)

      const del = document.createElement("button")
      del.type = "button"
      del.className = "ml-auto text-xs text-neutral-400 hover:text-rose-600 w-6 h-6 shrink-0"
      del.setAttribute("aria-label", "Удалить проём")
      del.textContent = "✕"
      del.addEventListener("click", () => {
        this.#pushUndo()
        this.#setOpenings(removeOpening(this.#openings(), op.id))
        if (this.selectedOpeningId === op.id) this.selectedOpeningId = null
        this.#renderOpenings()
        this.#renderOpeningPanel()
      })
      row.appendChild(del)

      list.appendChild(row)
    })
  }

  #estimate() {
    const floor = this.design.floors[0]
    const e = this.design.estimate
    return computeEstimate({
      graph: floor.graph,
      footprint: floor.footprint,
      openings: floor.openings,
      construction: this.design.construction,
      selections: e.selections,
      prices: e.prices,
      params: e.params,
    })
  }

  // Material/cost estimate panel (Stage 14d): a material <select> per category,
  // each line showing quantity + an editable unit price + cost, plus a total.
  // Built with DOM APIs. Price edits update numbers IN PLACE (no replaceChildren)
  // so the input firing its blur/change isn't detached mid-event.
  #renderEstimate() {
    if (!this.hasEstimateListTarget) return
    const list = this.estimateListTarget
    list.replaceChildren()
    this._estimateCostEls = new Map()
    if (!this.#graph().walls.length) {
      const empty = document.createElement("p")
      empty.className = "px-3 py-3 text-xs text-neutral-500"
      empty.textContent = "Смета появится, когда вы нарисуете стены."
      list.appendChild(empty)
      return
    }
    const est = this.#estimate()
    const e = this.design.estimate

    // Total FIRST in the section (right under the header), so the headline number
    // is visible the moment you reach the estimate — no scrolling to the bottom.
    // Not sticky: it would fight the other sticky section headers in the shared
    // aside scroll at the same top-0.
    const totalRow = document.createElement("div")
    totalRow.className = "px-3 py-2 bg-white border-b border-neutral-200 flex items-center justify-between text-sm font-semibold text-neutral-800"
    const tl = document.createElement("span")
    tl.textContent = "Итого ≈"
    const tv = document.createElement("span")
    tv.className = "tabular-nums text-indigo-700"
    tv.textContent = `${fmtMoney(est.total)} ₽`
    this._estimateTotalEl = tv
    totalRow.append(tl, tv)
    list.appendChild(totalRow)

    for (const cat of ESTIMATE_CATEGORIES) {
      const block = document.createElement("div")
      block.className = "px-3 py-2"

      const select = document.createElement("select")
      select.className = "w-full text-xs border border-neutral-300 rounded px-1.5 py-1 bg-white"
      select.setAttribute("aria-label", cat.label)
      for (const m of CATALOGUES[cat.key]) {
        const opt = document.createElement("option")
        opt.value = m.id
        opt.textContent = m.name
        if (m.id === e.selections[cat.key]) opt.selected = true
        select.appendChild(opt)
      }
      select.addEventListener("change", () => {
        e.selections[cat.key] = select.value
        this.#renderEstimate() // full rebuild — select change doesn't blur-detach
      })
      block.appendChild(select)

      for (const line of est.lines.filter((l) => l.category === cat.key)) {
        block.appendChild(this.#estimateLineRow(line))
      }
      list.appendChild(block)
    }

    const note = document.createElement("p")
    note.className = "px-3 py-2 text-xs text-neutral-400"
    note.textContent = "Ориентировочно: объёмы по осям стен за вычетом проёмов, цены — справочные 2026 г., уточняйте в регионе. Не заменяет смету проекта."
    list.appendChild(note)
  }

  #estimateLineRow(line) {
    const row = document.createElement("div")
    row.className = "mt-1 flex items-center gap-1.5 text-xs text-neutral-600"

    const qty = document.createElement("span")
    qty.className = "tabular-nums shrink-0 w-20"
    qty.textContent = `${fmtNum(line.quantity)} ${line.unit}`
    row.appendChild(qty)

    const price = document.createElement("input")
    price.type = "number"
    price.min = "0"
    price.step = "1"
    price.inputMode = "decimal"
    price.value = String(line.unitPrice)
    price.className = "w-16 text-xs border border-neutral-200 rounded px-1 py-0.5 tabular-nums"
    price.setAttribute("aria-label", `Цена за ед.: ${line.name}`)
    price.addEventListener("change", () => {
      const v = parseFloat(price.value)
      if (Number.isFinite(v) && v >= 0) {
        this.design.estimate.prices[line.key] = v
        this.#refreshEstimateNumbers() // in-place — don't rebuild the inputs
      }
    })
    row.appendChild(price)

    const per = document.createElement("span")
    per.className = "text-neutral-400 shrink-0"
    per.textContent = "₽"
    row.appendChild(per)

    const cost = document.createElement("span")
    cost.className = "ml-auto tabular-nums shrink-0 text-neutral-700"
    cost.textContent = `${fmtMoney(line.cost)} ₽`
    this._estimateCostEls.set(line.key, cost)
    row.appendChild(cost)

    return row
  }

  // Recompute and update only the cost cells + total text — leaves selects/price
  // inputs (and focus) intact, so a price edit firing on blur can't detach itself.
  #refreshEstimateNumbers() {
    if (!this._estimateCostEls) return
    const est = this.#estimate()
    for (const line of est.lines) {
      const el = this._estimateCostEls.get(line.key)
      if (el) el.textContent = `${fmtMoney(line.cost)} ₽`
    }
    if (this._estimateTotalEl) this._estimateTotalEl.textContent = `${fmtMoney(est.total)} ₽`
  }

  // Whole-plot direct-sun heatmap for per-room insolation, computed once per
  // open (deferred). The editing house is EXCLUDED from the shadow casters so
  // its own roof doesn't black out every interior cell — this measures each
  // room area's solar exposure by position (orientation/neighbours/latitude/
  // season). Skipped when the plot has no geographic origin (no sun model).
  #computeInsolation() {
    this._heatmap = null
    this._insolationObj = null
    this._sunSamples = null
    const obj = editorStore.objects.find((o) => o.id === this.houseId)
    const plot = editorStore.plot
    const origin = plot?.originLngLat
    if (!obj || !Array.isArray(plot?.polygonM) || plot.polygonM.length < 3 || !Array.isArray(origin)) return
    const override = editorStore.sun?.utcOffsetHoursOverride
    const utcOffsetHours = override != null ? override : estimateUtcOffsetHours(origin[0])
    this._heatmap = computeHeatmap({
      plot,
      objects: editorStore.objects.filter((o) => o.id !== this.houseId),
      lat: origin[1],
      lng: origin[0],
      dateIso: editorStore.sun.dateIso,
      utcOffsetHours,
      resolutionM: 0.5,
      stepMinutes: 15,
    })
    // Daylight sun-direction samples for window orientation (Stage 9c). Same
    // 15-min cadence as the heatmap so window hours and positional hours are
    // measured on the same day.
    this._sunStep = 15
    this._sunSamples = sunArcSamples(editorStore.sun.dateIso, origin[1], origin[0], utcOffsetHours, this._sunStep)
    this._insolationObj = obj
  }

  // Per-room sun diagnosis combining the room's POSITIONAL solar potential (the
  // heatmap average over its footprint) with the orientation of its EXTERIOR
  // windows. A room only gets the daylight its windows admit, so the effective
  // sun is the lesser of the two; a windowless room reports no sun at all.
  // Returns { sunHours, level, hasWindows } or null when no sun model is loaded.
  #roomSun(room) {
    if (!this._heatmap || !this._insolationObj) return null
    const floor = this.design.floors[0]
    const plotPoly = localToPlotPolygon(room.polygon, this._insolationObj, this.#planAnchor())
    const pos = computeRoomInsolation(this._heatmap, plotPoly)
    if (pos.level === "none") return { ...pos, hasWindows: true } // too small to grade
    const ws = roomWindowSun({
      roomPolygon: room.polygon,
      footprint: floor.footprint,
      openings: floor.openings,
      graph: floor.graph,
      rotationDeg: this._insolationObj.rotationDeg ?? 0,
      sunSamples: this._sunSamples,
      stepMinutes: this._sunStep,
    })
    if (ws.windows === 0) return { sunHours: 0, level: "low", hasWindows: false }
    const sunHours = Math.min(pos.sunHours, ws.hours)
    return { sunHours, level: insolationLevel(sunHours), hasWindows: true }
  }

  // Advisor fit for a room: how well its position's time-of-day sun matches its
  // type's profile (morning/day/afternoon). Positional (placement) — independent
  // of windows. Returns { score, good, label } or null when no sun model.
  #roomFit(room) {
    if (!this._heatmap || !this._insolationObj) return null
    const plotPoly = localToPlotPolygon(room.polygon, this._insolationObj, this.#planAnchor())
    const bh = bandHoursInPolygon(this._heatmap, plotPoly)
    if (bh.cells === 0) return null
    return fitFor(bh, room.type)
  }

  // Advisor overlay: when a room type is armed, highlight the footprint positions
  // whose time-of-day sun best suits that type (top quartile of a positive
  // score). Sampled on a 0.5 m local grid, each point mapped into the plot frame
  // to read the banded heatmap. Drawn as translucent emerald dots.
  #renderAdvisor() {
    if (!this.advisorLayer) return
    this.advisorLayer.destroyChildren()
    this._advisorSpotCount = 0
    if (!this.advisorType || !this._heatmap || !this._insolationObj || !this.scale) {
      this.advisorLayer.batchDraw()
      return
    }
    const floor = this.design.floors[0]
    const footprint = floor.footprint
    if (!Array.isArray(footprint) || footprint.length < 3) { this.advisorLayer.batchDraw(); return }
    const anchor = this.#planAnchor()
    const profile = profileFor(this.advisorType)
    let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity
    for (const [x, y] of footprint) {
      if (x < minX) minX = x; if (x > maxX) maxX = x
      if (y < minY) minY = y; if (y > maxY) maxY = y
    }
    const step = 0.5
    const cands = []
    for (let y = minY; y <= maxY; y += step) {
      for (let x = minX; x <= maxX; x += step) {
        if (!pointInPolygon(x, y, footprint)) continue
        const p = localToPlotPolygon([[x, y]], this._insolationObj, anchor)[0]
        const bh = bandHoursAt(this._heatmap, p[0], p[1])
        if (!bh) continue
        cands.push({ x, y, score: scorePlacement(bh, profile) })
      }
    }
    const best = cands.reduce((m, c) => (c.score > m ? c.score : m), -Infinity)
    if (!cands.length || best <= 0) { this.advisorLayer.batchDraw(); return }
    // Suppress when the plot is uniformly lit (no shadows/obstructions): every
    // cell scores nearly the same, so "best spots" would be the whole house — a
    // misleading non-hint. Differentiation appears once there are shadow casters
    // (neighbours — Stage 8b) or an irregular footprint.
    const mean = cands.reduce((s, c) => s + c.score, 0) / cands.length
    const variance = cands.reduce((s, c) => s + (c.score - mean) ** 2, 0) / cands.length
    if (Math.sqrt(variance) / best < 0.05) { this.advisorLayer.batchDraw(); return }
    const thresh = best * 0.75
    const spots = cands.filter((c) => c.score >= thresh)
    this._advisorSpotCount = spots.length
    const r = Math.max(4, step * this.scale.pxPerMetre * 0.45)
    for (const s of spots) {
      const p = metresToPx({ xM: s.x, yM: s.y }, this.scale)
      this.advisorLayer.add(new Konva.Circle({ x: p.x, y: p.y, radius: r, fill: "rgba(16,185,129,0.45)", listening: false }))
    }
    this.advisorLayer.batchDraw()
  }

  #onVertexDragMove(vertexId, dot) {
    // Live-follow connected walls WITHOUT mutating the graph — the authoritative
    // move is applied once on dragend via moveVertex(). Keeping the working
    // graph immutable mid-drag means an interrupted drag (Escape / teardown)
    // commits the last settled state, never a half-dragged position. The
    // dragged vertex's pixels come straight from the Konva dot; the other
    // endpoints from the (unchanged) graph.
    const g = this.#graph()
    const byId = new Map(g.vertices.map((x) => [x.id, x]))
    let live = dot.position()
    // Snap the live position onto a neighbour's axis (→ ortho wall) or another
    // vertex's column/row (→ alignment). Reposition the dot so it visibly clicks
    // into place, and surface guide lines for the snapped axes.
    let guideX = null, guideY = null
    if (this._snapEnabled) {
      const m = pxToMetres(live, this.scale)
      const tolM = this.#snapTolM()
      const snap = snapDraggedVertex(g, vertexId, m.xM, m.yM, tolM)
      if (snap.guideX != null || snap.guideY != null) {
        live = metresToPx({ xM: snap.xM, yM: snap.yM }, this.scale)
        dot.position(live)
        guideX = snap.guideX
        guideY = snap.guideY
      }
    }
    const pxOf = (vid) => {
      if (vid === vertexId) return { x: live.x, y: live.y }
      const v = byId.get(vid)
      return v ? metresToPx({ xM: v.xM, yM: v.yM }, this.scale) : null
    }
    for (const line of this.wallLayer.getChildren()) {
      const w = g.walls.find((x) => x.id === line.getAttr("wallId"))
      if (!w) continue
      const pa = pxOf(w.a)
      const pb = pxOf(w.b)
      if (!pa || !pb) continue
      line.points([pa.x, pa.y, pb.x, pb.y])
    }
    this.#renderDragGuides(guideX, guideY)
    this.wallLayer.batchDraw()
  }

  #onVertexDragEnd(vertexId, dot) {
    // Clear the alignment guides drawn during the drag.
    this.previewLayer.destroyChildren()
    this.previewLayer.batchDraw()
    const m = pxToMetres(dot.position(), this.scale)
    let fx = snapM(m.xM), fy = snapM(m.yM)
    // Honour the live snap on commit: a snapped axis lands EXACTLY on the
    // neighbour/alignment target (not re-rounded to the 0.1 m grid).
    if (this._snapEnabled) {
      const snap = snapDraggedVertex(this.#graph(), vertexId, m.xM, m.yM, this.#snapTolM())
      if (snap.guideX != null) fx = snap.xM
      if (snap.guideY != null) fy = snap.yM
    }
    // settleVertex welds onto a coincident vertex and re-splits T-junctions, so
    // dragging a corner onto another can't leave degenerate zero-length walls.
    this.#setGraph(settleVertex(this.#graph(), vertexId, fx, fy))
    this.selectedWallId = null
    this.#render()
  }

  // Snap tolerance in metres: ~8 px on screen, never below 0.1 m, so it feels
  // consistent at any zoom.
  #snapTolM() {
    return Math.max(0.1, 8 / (this.scale?.pxPerMetre || 1))
  }

  // Fuchsia dashed alignment guides spanning the content frame, drawn on the
  // (non-listening) previewLayer while a vertex drag is snapping.
  #renderDragGuides(guideX, guideY) {
    this.previewLayer.destroyChildren()
    if (this.scale && this._frame) {
      const f = this._frame
      const col = "#d946ef" // fuchsia-500
      if (guideX != null) {
        const a = metresToPx({ xM: guideX, yM: f.minY - 4 }, this.scale)
        const b = metresToPx({ xM: guideX, yM: f.maxY + 4 }, this.scale)
        this.previewLayer.add(new Konva.Line({ points: [a.x, a.y, b.x, b.y], stroke: col, strokeWidth: 1, dash: [4, 4] }))
      }
      if (guideY != null) {
        const a = metresToPx({ xM: f.minX - 4, yM: guideY }, this.scale)
        const b = metresToPx({ xM: f.maxX + 4, yM: guideY }, this.scale)
        this.previewLayer.add(new Konva.Line({ points: [a.x, a.y, b.x, b.y], stroke: col, strokeWidth: 1, dash: [4, 4] }))
      }
    }
    this.previewLayer.batchDraw()
  }

  // 1 m reference grid across the content frame (+2 m margin), with a darker
  // line every 5 m. Gives the plan a sense of scale instead of floating boxes.
  #renderGrid() {
    this.gridLayer.destroyChildren()
    if (!this.scale || !this._frame) { this.gridLayer.batchDraw(); return }
    const f = this._frame
    const x0 = Math.floor(f.minX - 2), x1 = Math.ceil(f.maxX + 2)
    const y0 = Math.floor(f.minY - 2), y1 = Math.ceil(f.maxY + 2)
    const top = metresToPx({ xM: x0, yM: y0 }, this.scale)
    const bot = metresToPx({ xM: x1, yM: y1 }, this.scale)
    for (let x = x0; x <= x1; x++) {
      const px = metresToPx({ xM: x, yM: 0 }, this.scale).x
      this.gridLayer.add(new Konva.Line({
        points: [px, top.y, px, bot.y],
        stroke: x % 5 === 0 ? GRID_MAJOR : GRID_MINOR,
        strokeWidth: 1,
        listening: false,
      }))
    }
    for (let y = y0; y <= y1; y++) {
      const py = metresToPx({ xM: 0, yM: y }, this.scale).y
      this.gridLayer.add(new Konva.Line({
        points: [top.x, py, bot.x, py],
        stroke: y % 5 === 0 ? GRID_MAJOR : GRID_MINOR,
        strokeWidth: 1,
        listening: false,
      }))
    }
    this.gridLayer.batchDraw()
  }

  // A small length pill at each wall's midpoint, aligned with the wall, so the
  // plan reads as a measurable drawing rather than abstract bars. In edit mode
  // the pills become tappable — tapping one opens the length popover for an
  // exact numeric edit (Stage 14b.1), so the whole layer only listens then.
  #renderWallLabels() {
    this.labelLayer.destroyChildren()
    const editable = this.mode === "edit" && !this._readOnly
    this.labelLayer.listening(editable)
    const g = this.#graph()
    const byId = new Map(g.vertices.map((v) => [v.id, v]))
    for (const w of g.walls) {
      const a = byId.get(w.a)
      const b = byId.get(w.b)
      if (!a || !b) continue
      const lenM = Math.hypot(b.xM - a.xM, b.yM - a.yM)
      if (lenM < 0.3) continue
      const mid = metresToPx({ xM: (a.xM + b.xM) / 2, yM: (a.yM + b.yM) / 2 }, this.scale)
      let deg = (Math.atan2(b.yM - a.yM, b.xM - a.xM) * 180) / Math.PI
      if (deg > 90) deg -= 180
      else if (deg < -90) deg += 180
      const editing = editable && w.id === this._lenWallId
      const text = new Konva.Text({
        text: `${fmt(lenM)} м`,
        fontSize: 11,
        fontFamily: "Nunito, sans-serif",
        fontStyle: "600",
        fill: editing ? "#ffffff" : "#334155",
        padding: 3,
        listening: false,
      })
      const tw = text.width()
      const th = text.height()
      text.offsetX(tw / 2)
      text.offsetY(th / 2)
      // A roomier hit pill than the visible text so a finger can land it —
      // larger still on coarse (touch) pointers.
      const hitPad = editable ? (this._coarsePointer ? 12 : 6) : 0
      const bg = new Konva.Rect({
        width: tw + hitPad * 2,
        height: th + hitPad * 2,
        offsetX: tw / 2 + hitPad,
        offsetY: th / 2 + hitPad,
        fill: editing ? "rgba(79,70,229,0.92)" : "rgba(255,255,255,0.9)",
        stroke: editable && !editing ? "#c7d2fe" : undefined,
        strokeWidth: editable && !editing ? 1 : 0,
        cornerRadius: 3,
      })
      const grp = new Konva.Group({ x: mid.x, y: mid.y, rotation: deg, listening: editable })
      grp.add(bg, text)
      if (editable) {
        grp.setAttr("wallId", w.id)
        grp.on("click tap", (e) => {
          e.cancelBubble = true
          this.#openLengthPopover(w.id)
        })
      }
      this.labelLayer.add(grp)
    }
    this.labelLayer.batchDraw()
  }

  // ---- wall length popover (Stage 14b.1) ----
  // Tap a wall's length pill (edit mode) → enter an exact length → the wall is
  // resized along its own axis (resizeWallAlongAxis). Mirrors the plot edge
  // editor, but lives inside this overlay rather than a separate controller.

  #lengthPopoverOpen() {
    return this.hasLengthPopoverTarget && !this.lengthPopoverTarget.classList.contains("hidden")
  }

  #openLengthPopover(wallId) {
    if (!this.hasLengthPopoverTarget) return
    const g = this.#graph()
    const w = g.walls.find((x) => x.id === wallId)
    if (!w) return
    const byId = new Map(g.vertices.map((v) => [v.id, v]))
    const a = byId.get(w.a), b = byId.get(w.b)
    if (!a || !b) return
    const lenM = Math.hypot(b.xM - a.xM, b.yM - a.yM)
    // Anchor the popover at the wall's midpoint in viewport coords — computed
    // from the wall itself (not a label group), so it also works when opened
    // from a double-click or Enter on a selected wall. getAbsoluteTransform
    // folds in the stage zoom/pan.
    const pmid = metresToPx({ xM: (a.xM + b.xM) / 2, yM: (a.yM + b.yM) / 2 }, this.scale)
    const absT = this.stage.getAbsoluteTransform().point(pmid)
    const rect = this.canvasTarget.getBoundingClientRect()
    const anchorPt = { x: rect.left + absT.x, y: rect.top + absT.y }

    this._lenWallId = wallId
    this._lenAnchor = "a" // default: the far end (b) slides
    this.lengthPopoverTarget.classList.remove("hidden")
    this.lengthInputTarget.value = this.#formatLen(lenM)
    this.#clearLengthError()
    this.#renderLengthAnchor()

    const raf = typeof requestAnimationFrame === "function" ? requestAnimationFrame : (cb) => setTimeout(cb, 16)
    raf(() => {
      this.#positionLengthPopover(anchorPt.x, anchorPt.y)
      // Defer the highlight re-render out of the pill's own click dispatch —
      // #renderWallLabels destroys the group currently handling the tap.
      this.#renderWallLabels()
    })
    this._lenFocusTimer = setTimeout(() => {
      this._lenFocusTimer = null
      if (!this.#lengthPopoverOpen()) return
      this.lengthInputTarget.focus({ preventScroll: true })
      this.lengthInputTarget.select()
    }, 30)
  }

  applyLength(e) {
    e?.preventDefault?.()
    if (!this.#lengthPopoverOpen()) return
    const raw = (this.lengthInputTarget.value || "").replace(",", ".").trim()
    const len = parseFloat(raw)
    if (!isFinite(len) || len <= 0) { this.#showLengthError("Введите положительное число"); return }
    if (len > 1000) { this.#showLengthError("Слишком большое значение"); return }
    if (len < MIN_ROOM_M) { this.#showLengthError(`Не короче ${MIN_ROOM_M} м`); return }
    const next = resizeWallAlongAxis(this.#graph(), this._lenWallId, len, this._lenAnchor)
    if (!next) { this.#showLengthError("Не удалось пересчитать"); return }
    this.#pushUndo()
    this.#setGraph(next)
    this.#closeLengthPopover()
    this.#fit()
    this.#render()
  }

  toggleLengthAnchor() {
    this._lenAnchor = this._lenAnchor === "a" ? "b" : "a"
    this.#renderLengthAnchor()
    this.#scheduleLengthPreview()
  }

  lengthInputChanged() {
    this.#clearLengthError()
    this.#scheduleLengthPreview()
  }

  lengthKeydown(e) {
    if (e.key === "Enter") { e.preventDefault(); this.applyLength() }
  }

  // Public alias for the popover's Cancel button (data-action needs a public name).
  cancelLength() {
    this.#closeLengthPopover()
    this.#renderWallLabels()
  }

  #closeLengthPopover() {
    this._lenWallId = null
    this._lenAnchor = "a"
    if (this._lenFocusTimer) { clearTimeout(this._lenFocusTimer); this._lenFocusTimer = null }
    this.#clearLengthPreview()
    if (this.hasLengthPopoverTarget) this.lengthPopoverTarget.classList.add("hidden")
  }

  #renderLengthAnchor() {
    if (!this.hasLengthAnchorTarget) return
    // anchor "a" → far end (b) slides; anchor "b" → start end (a) slides.
    this.lengthAnchorTarget.textContent = this._lenAnchor === "a" ? "тянуть конец →" : "← тянуть начало"
    this.lengthAnchorTarget.setAttribute(
      "aria-label",
      this._lenAnchor === "a" ? "Сдвигать дальний конец стены" : "Сдвигать ближний конец стены",
    )
  }

  #showLengthError(msg) {
    if (!this.hasLengthErrorTarget) return
    this.lengthErrorTarget.textContent = msg
    this.lengthErrorTarget.classList.remove("hidden")
    if (this.hasLengthApplyTarget) this.lengthApplyTarget.disabled = true
  }

  #clearLengthError() {
    if (this.hasLengthErrorTarget) {
      this.lengthErrorTarget.textContent = ""
      this.lengthErrorTarget.classList.add("hidden")
    }
    if (this.hasLengthApplyTarget) this.lengthApplyTarget.disabled = false
  }

  #positionLengthPopover(vx, vy) {
    if (!this.hasLengthPopoverTarget) return
    const rect = this.lengthPopoverTarget.getBoundingClientRect()
    const pad = 8
    const w = rect.width || 224
    const h = rect.height || 150
    const below = vy + 16
    const above = vy - h - 16
    const y = (below + h + pad > window.innerHeight && above > 60) ? above : below
    const desiredLeft = vx - w / 2
    const maxLeft = Math.max(pad, window.innerWidth - w - pad)
    const left = Math.max(pad, Math.min(maxLeft, desiredLeft))
    this.lengthPopoverTarget.style.top = `${y}px`
    this.lengthPopoverTarget.style.left = `${left}px`
  }

  #scheduleLengthPreview() {
    if (this._lenPreviewTimer) clearTimeout(this._lenPreviewTimer)
    this._lenPreviewTimer = setTimeout(() => this.#emitLengthPreview(), 120)
  }

  // Dashed ghost of where the edited wall will sit at the typed length — drawn
  // on the (non-listening) previewLayer, which is otherwise only used by the
  // room-tool rubber-band (inactive in edit mode).
  #emitLengthPreview() {
    if (!this.#lengthPopoverOpen() || !this.scale) return
    const g = this.#graph()
    const w = g.walls.find((x) => x.id === this._lenWallId)
    if (!w) { this.#clearLengthPreview(); return }
    const byId = new Map(g.vertices.map((v) => [v.id, v]))
    const a = byId.get(w.a), b = byId.get(w.b)
    if (!a || !b) { this.#clearLengthPreview(); return }
    const len = parseFloat((this.lengthInputTarget.value || "").replace(",", ".").trim())
    if (!isFinite(len) || len <= 0) { this.#clearLengthPreview(); return }
    const cur = Math.hypot(b.xM - a.xM, b.yM - a.yM)
    if (cur === 0) { this.#clearLengthPreview(); return }
    const ux = (b.xM - a.xM) / cur, uy = (b.yM - a.yM) / cur
    const p0 = this._lenAnchor === "b" ? b : a
    const sign = this._lenAnchor === "b" ? -1 : 1
    const p1 = { xM: p0.xM + sign * ux * len, yM: p0.yM + sign * uy * len }
    const a0 = metresToPx({ xM: p0.xM, yM: p0.yM }, this.scale)
    const a1 = metresToPx(p1, this.scale)
    this.previewLayer.destroyChildren()
    this.previewLayer.add(new Konva.Line({
      points: [a0.x, a0.y, a1.x, a1.y], stroke: "#4f46e5", strokeWidth: 2, dash: [6, 4], lineCap: "round",
    }))
    this.previewLayer.add(new Konva.Circle({ x: a1.x, y: a1.y, radius: 4, fill: "#4f46e5" }))
    this.previewLayer.batchDraw()
  }

  #clearLengthPreview() {
    if (this._lenPreviewTimer) { clearTimeout(this._lenPreviewTimer); this._lenPreviewTimer = null }
    if (this.previewLayer) { this.previewLayer.destroyChildren(); this.previewLayer.batchDraw() }
  }

  #formatLen(lenM) {
    let s
    if (lenM < 10) s = lenM.toFixed(2)
    else if (lenM < 100) s = lenM.toFixed(1)
    else s = String(Math.round(lenM))
    if (s.includes(".")) s = s.replace(/0+$/, "").replace(/\.$/, "")
    return s
  }

  #renderPreview(start, end) {
    this.previewLayer.destroyChildren()
    const x = Math.min(start.xM, end.xM)
    const y = Math.min(start.yM, end.yM)
    const wM = Math.abs(end.xM - start.xM)
    const hM = Math.abs(end.yM - start.yM)
    const p = metresToPx({ xM: x, yM: y }, this.scale)
    this.previewLayer.add(new Konva.Rect({
      x: p.x,
      y: p.y,
      width: wM * this.scale.pxPerMetre,
      height: hM * this.scale.pxPerMetre,
      stroke: "#4f46e5",
      strokeWidth: 1.5,
      dash: [6, 4],
      fill: "rgba(79,70,229,0.08)",
    }))
    if (wM >= 0.1 && hM >= 0.1) {
      const label = new Konva.Text({
        text: `${fmt(wM)} × ${fmt(hM)} м`,
        x: p.x,
        y: p.y - 18,
        fontSize: 12,
        fontFamily: "Nunito, sans-serif",
        fontStyle: "700",
        fill: "#3730a3",
      })
      this.previewLayer.add(label)
    }
    this.previewLayer.batchDraw()
  }

  #updateReadout() {
    if (!this.hasReadoutTarget) return
    const g = this.#graph()
    if (!g.walls.length) {
      this.readoutTarget.textContent = "Пусто — нарисуйте первую комнату"
      return
    }
    const bb = boundingBox(g)
    this.readoutTarget.textContent =
      `Контур ${fmt(bb.widthM)}×${fmt(bb.heightM)} м · стен ${fmt(totalWallLengthM(g))} м`
  }

  #teardown() {
    this.#destroyStage()
    if (this._raf) {
      cancelAnimationFrame(this._raf)
      this._raf = null
    }
    if (this._insolTimer) {
      clearTimeout(this._insolTimer)
      this._insolTimer = null
    }
    if (this._lenPreviewTimer) {
      clearTimeout(this._lenPreviewTimer)
      this._lenPreviewTimer = null
    }
    // Hide the popover + clear its focus timer (disconnect() reaches teardown
    // directly, bypassing close()'s #closeLengthPopover).
    this.#closeLengthPopover()
    this._heatmap = null
    this._insolationObj = null
    this._sunSamples = null
    document.removeEventListener("keydown", this._onKeyDown)
    document.body.classList.remove("house-mode-open")
    if (this.hasModalTarget) {
      this.modalTarget.classList.add("hidden")
      this.modalTarget.setAttribute("aria-hidden", "true")
    }
    this.houseId = null
  }
}

function snapM(x) {
  return Math.round(x / SNAP_M) * SNAP_M
}

function fmt(n) {
  const r = Math.round(n * 10) / 10
  return Number.isInteger(r) ? String(r) : r.toFixed(1)
}

// Quantity: integers plain, fractions to one decimal.
function fmtNum(n) {
  return Number.isInteger(n) ? String(n) : (Math.round(n * 10) / 10).toFixed(1)
}

// Money: thousands separated with thin spaces (ru), no decimals.
function fmtMoney(n) {
  return Math.round(n).toLocaleString("ru-RU")
}
